/*
  Product recommendation lifecycle owner — R44-263.
  Product-only asset. Owns recommendation fetch, complementary carousels,
  Related Products Flickity, teardown, and the trace-proven Android scroll guard.
*/
(function () {
  'use strict';

  if (customElements.get('product-recommendations')) return;

  class ProductRecommendations extends HTMLElement {
    constructor() {
      super();
      this.observer = null;
      this.abortController = null;
      this.idleCallbackId = null;
      this.loadHandler = null;
      this.relatedCarouselTimer = null;
    }

    connectedCallback() {
      if (this.dataset.recommendationsConnected === 'true') return;
      this.dataset.recommendationsConnected = 'true';

      if (this.dataset.manual === 'true') return;
      const productId = this.dataset.productId;
      if (!productId) return;

      if (this.classList.contains('complementary-products')) {
        this.classList.add('product-recommendations--pending');
        this.setAttribute('aria-busy', 'true');
        this.scheduleComplementaryLoad(productId);
        return;
      }

      /*
        R44-257 contract: Related Products must not be scroll-triggered.
        Starting the request immediately prevents the lower-PDP viewport from
        becoming the trigger for its asynchronous DOM insertion.
      */
      if (this.classList.contains('related-products')) {
        this.loadRecommendations(productId);
        return;
      }

      this.initializeRecommendations(productId);
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      if (this.idleCallbackId !== null && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(this.idleCallbackId);
      }
      this.idleCallbackId = null;
      if (this.loadHandler) window.removeEventListener('load', this.loadHandler);
      this.loadHandler = null;
      if (this.relatedCarouselTimer) window.clearTimeout(this.relatedCarouselTimer);
      this.relatedCarouselTimer = null;
      delete this.dataset.recommendationsConnected;
    }

    scheduleComplementaryLoad(productId) {
      const load = () => {
        this.idleCallbackId = null;
        this.loadHandler = null;
        if (this.isConnected) this.loadRecommendations(productId);
      };

      if ('requestIdleCallback' in window) {
        this.idleCallbackId = window.requestIdleCallback(load, { timeout: 450 });
      } else if (document.readyState === 'complete') {
        window.setTimeout(load, 0);
      } else {
        this.loadHandler = load;
        window.addEventListener('load', load, { once: true });
      }
    }

    initializeRecommendations(productId) {
      if (this.dataset.manual === 'true') return;
      if (!('IntersectionObserver' in window)) {
        this.loadRecommendations(productId);
        return;
      }

      if (this.observer) this.observer.disconnect();
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0]?.isIntersecting) return;
          this.observer?.disconnect();
          this.observer = null;
          this.loadRecommendations(productId);
        },
        { rootMargin: '0px 0px 600px 0px' }
      );
      this.observer.observe(this);
    }

    initializeComplementaryCarousels() {
      if (!this.classList.contains('complementary-products')) return Promise.resolve();

      const lists = Array.from(this.querySelectorAll('.complementary-slide .list-unstyled'));
      const needsCarousel = lists.some((list) => list.children.length > 2 && !list.classList.contains('flickity-enabled'));
      if (!needsCarousel) return Promise.resolve();

      return new Promise((resolve) => {
        const startedAt = performance.now();
        const initialize = () => {
          if (!this.isConnected) {
            resolve();
            return;
          }

          if (typeof window.Flickity === 'function') {
            lists.forEach((list) => {
              if (list.children.length <= 2 || list.classList.contains('flickity-enabled')) return;
              new window.Flickity(list, {
                cellAlign: 'left',
                contain: true,
                pageDots: false,
                wrapAround: true,
                percentPosition: true,
              });
            });
            resolve();
            return;
          }

          if (performance.now() - startedAt >= 750) {
            resolve();
            return;
          }
          window.setTimeout(initialize, 25);
        };
        initialize();
      });
    }

    initializeRelatedCarousel(attempt = 0) {
      if (!this.classList.contains('related-products') || !this.isConnected) return;
      const list = this.querySelector('.related-products-slider');
      if (!list || list.classList.contains('flickity-enabled')) return;

      if (typeof window.Flickity === 'function') {
        new window.Flickity(list, {
          cellAlign: 'left',
          contain: true,
          pageDots: false,
          wrapAround: false,
          percentPosition: true,
          cellSelector: '.grid__item:not([hidden])',
        });
        return;
      }

      if (attempt >= 30) return;
      this.relatedCarouselTimer = window.setTimeout(() => {
        this.relatedCarouselTimer = null;
        this.initializeRelatedCarousel(attempt + 1);
      }, 25);
    }

    preserveRelatedViewportDuringInsertion(insert) {
      const shouldGuard = this.classList.contains('related-products') && window.scrollY > 150;
      if (!shouldGuard) {
        insert();
        return;
      }

      const preservedScrollY = window.scrollY;
      const root = document.documentElement;
      const previousOverflowAnchor = root.style.overflowAnchor;
      root.style.overflowAnchor = 'none';

      insert();

      const restoreIfBrowserReset = () => {
        const currentY = window.scrollY || 0;
        if (preservedScrollY > 150 && currentY < 60 && preservedScrollY - currentY > 120) {
          window.scrollTo({ top: preservedScrollY, left: 0, behavior: 'auto' });
        }
      };

      void this.offsetHeight;
      restoreIfBrowserReset();
      window.requestAnimationFrame(() => {
        restoreIfBrowserReset();
        window.requestAnimationFrame(() => {
          restoreIfBrowserReset();
          root.style.overflowAnchor = previousOverflowAnchor;
        });
      });
    }

    loadRecommendations(productId) {
      if (this.dataset.manual === 'true' || this.dataset.recommendationsLoading === 'true' || this.dataset.recommendationsLoaded === 'true') return;
      this.dataset.recommendationsLoading = 'true';
      if (this.abortController) this.abortController.abort();
      this.abortController = typeof window.AbortController === 'function' ? new window.AbortController() : null;
      const requestOptions = { credentials: 'same-origin' };
      if (this.abortController) requestOptions.signal = this.abortController.signal;

      fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`, requestOptions)
        .then((response) => {
          if (!response.ok) throw new Error(`Product recommendations request failed: ${response.status}`);
          return response.text();
        })
        .then((text) => {
          if (!this.isConnected) return;
          const html = document.createElement('div');
          html.innerHTML = text;
          const recommendations = html.querySelector('product-recommendations');

          const hasCards = recommendations?.querySelector('[data-recommendation-product]');
          const preserve = this.dataset.preserveFallback === 'true';
          const userIsInteracting = this.contains(document.activeElement) || this.querySelector('details[open]');
          if (recommendations?.innerHTML.trim().length && (!preserve || hasCards) && !userIsInteracting) {
            this.preserveRelatedViewportDuringInsertion(() => {
              this.innerHTML = recommendations.innerHTML;
              if (preserve) {
                this.dataset.recommendationSource = recommendations.dataset.recommendationSource;
                this.dataset.recommendationCount = recommendations.dataset.recommendationCount;
              }
            });

            /* Device-family filters listen to this before Flickity is created. */
            this.dispatchEvent(new CustomEvent('product-recommendations:loaded', { bubbles: true }));
            this.initializeRelatedCarousel();
            this.dataset.recommendationsLoaded = 'true';
          }

          if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
            this.remove();
            return;
          }

          return this.initializeComplementaryCarousels().then(() => {
            if (html.querySelector('.grid__item')) this.classList.add('product-recommendations--loaded');
            this.classList.remove('product-recommendations--pending');
            this.setAttribute('aria-busy', 'false');
          });
        })
        .catch((error) => {
          if (error?.name === 'AbortError') return;
          if (this.classList.contains('complementary-products')) this.remove();
          else {
            this.dataset.recommendationState = 'fallback';
            this.setAttribute('aria-busy', 'false');
          }
        })
        .finally(() => {
          delete this.dataset.recommendationsLoading;
          this.abortController = null;
        });
    }
  }

  customElements.define('product-recommendations', ProductRecommendations);
})();
